import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { IconFileText, IconQrcode } from "@tabler/icons-react";
import { KeyConstant, LangConstant } from "const";
import { BranchUtils, StorageUtils, SystemUtils } from "utils";
import { useUpdateBranch } from "hooks";
import { PaperHeader } from "components";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import CreateQRDialog from "./CreateQRDialog";
import PreviewInformationDialog from "./PreviewInformationDialog";
import { useGetBranchQR } from "hooks";

const ServerInformation = () => {
  const { t: getLabel } = useTranslation();
  const { updateBranchInfo } = useUpdateBranch();

  const currentBranchInformation = StorageUtils.getPermanenceData(KeyConstant.KEY_BRANCH_INFORMATION);
  const currentBranchExpiryTime = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_QR_EXPIRY_TIME);
  const { getBranchQR } = useGetBranchQR();

  const { quill, quillRef } = useQuill({
    modules: {
      toolbar: [
        [{ size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link", "image", "video"],
        [{ color: [] }],
      ],
      clipboard: {
        matchVisual: false,
      },
    },
  });

  const [contentInformation, setContentInformation] = useState(currentBranchInformation);
  const [msgQR, setMsgQR] = useState("");
  const [isQRExpired, setIsQRExpired] = useState(false);
  const [isOpenQRDialog, setIsOpenQRDialog] = useState(false);
  const [isOpenPreview, setIsOpenPreview] = useState(false);
  const [dataQR, setDataQR] = useState(null);

  const handleSubmit = event => {
    event.preventDefault();
    const currentBranchDetails = BranchUtils.getCurrentBranch();
    const payload = { ...currentBranchDetails, information: contentInformation, isUpdateBranchConfigs: false };
    updateBranchInfo(payload);
  };

  const downloadQR = () => {
    const link = document.createElement("a");
    link.href = dataQR;
    link.download = "qr.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (quill) {
      quill.clipboard.dangerouslyPasteHTML(contentInformation, "silent");
      quill.getModule("toolbar").addHandler("image", () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();
        input.onchange = () => {
          const file = input.files[0];
          if (file) {
            const fileSize = file.size;
            if (fileSize > 50 * 1024) {
              SystemUtils.appendNotification(getLabel(LangConstant.TXT_IMAGE_TOO_LARGE), "error");
              return;
            } else {
              const reader = new FileReader();
              reader.onload = e => {
                const range = quill.getSelection();
                quill.insertEmbed(range.index, "image", e.target.result);
              };
              reader.readAsDataURL(file);
            }
          }
        };
      });

      quill.on("text-change", () => {
        setContentInformation(quillRef.current.firstChild.innerHTML);
      });
    }
  }, [quill]);

  useEffect(() => {
    if (currentBranchExpiryTime) {
      if (currentBranchExpiryTime <= Date.now()) {
        setMsgQR(getLabel(LangConstant.TXT_QR_EXPIRED));
        setIsQRExpired(true);
      } else {
        const date = new Date(currentBranchExpiryTime);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        setMsgQR(getLabel(LangConstant.TXT_QR_VALID_UNTIL, { date: `${day}/${month}/${year}` }));
        setIsQRExpired(false);
      }
    }
  }, [currentBranchExpiryTime]);

  useEffect(() => {
    const fetchQrCode = async () => {
      if (currentBranchExpiryTime && currentBranchExpiryTime >= Date.now()) {
        const qr = await getBranchQR({ qr_expiry_time: currentBranchExpiryTime });
        setDataQR(qr);
      }
    };
    fetchQrCode();
  }, [currentBranchExpiryTime]);

  return (
    <div>
      <style>{`
        [data-bs-theme="dark"] .ql-toolbar.ql-snow {
          border-color: var(--tblr-border-color) !important;
          background: var(--tblr-bg-surface-secondary) !important;
        }
        [data-bs-theme="dark"] .ql-toolbar.ql-snow .ql-stroke {
          stroke: var(--tblr-body-color) !important;
        }
        [data-bs-theme="dark"] .ql-toolbar.ql-snow .ql-fill {
          fill: var(--tblr-body-color) !important;
        }
        [data-bs-theme="dark"] .ql-toolbar.ql-snow .ql-picker-label {
          color: var(--tblr-body-color) !important;
        }
        [data-bs-theme="dark"] .ql-container.ql-snow {
          border-color: var(--tblr-border-color) !important;
          background: var(--tblr-bg-surface) !important;
          color: var(--tblr-body-color) !important;
        }
        [data-bs-theme="dark"] .ql-editor {
          color: var(--tblr-body-color) !important;
        }
        [data-bs-theme="dark"] .ql-editor * {
          color: var(--tblr-body-color) !important;
        }
        [data-bs-theme="dark"] .ql-editor.ql-blank::before {
          color: var(--tblr-secondary) !important;
        }
        [data-bs-theme="dark"] .ql-snow .ql-picker-options {
          background: var(--tblr-bg-surface) !important;
          border-color: var(--tblr-border-color) !important;
        }
      `}</style>
      <div>
        <PaperHeader icon={<IconFileText size={24} className="text-primary" />} title={getLabel(LangConstant.TXT_SERVER_INFO)} />

        <div>
          <h4 className="mb-2">{getLabel(LangConstant.TXT_CONTENT)}</h4>
          <div style={{ width: "100%", height: "200px" }}>
            <div ref={quillRef} />
          </div>
        </div>

        <div className="d-flex justify-content-end mt-5 pt-3">
          <button className="btn btn-link" onClick={() => setIsOpenPreview(true)}>
            {getLabel(LangConstant.TXT_PREVIEW)}
          </button>
        </div>

        <div className="text-center">
          <button
            className="btn btn-primary"
            style={{ minWidth: "345px", minHeight: "46px" }}
            onClick={handleSubmit}
          >
            {getLabel(LangConstant.L_SAVE_BUTTON)}
          </button>
        </div>

        <PreviewInformationDialog
          open={isOpenPreview}
          onClose={() => setIsOpenPreview(false)}
          contentInformation={contentInformation}
        />
      </div>
    </div>
  );
};

export default ServerInformation;
